/**
 * @file StatusPill atom — a state chip: filled pill, status dot, status label.
 * @description Narrowed atom for the verdict chip the Financial & Compliance
 * surfaces put beside a section title — "BREACH · 3 alerts", "OK · compliant",
 * "NOT CONFIGURED" (SE-7002, Figma nodes 10:5 light / 141:656 dark in the
 * Financial & Compliance Dashboard file).
 *
 * Why an atom rather than a chip assembled in the feature: the pill radius is
 * budget-counted by a raw substring scan over every feature view, so a
 * hand-rolled pill books new design-system debt in whichever feature happens
 * to draw one first. Owning the shape here lets every compliance surface
 * render the same chip without touching that budget — and the Compliance tab
 * needs the identical chip, so a second hand-rolled copy was already coming.
 *
 * Tone is a CLOSED enum, unlike `Dot`'s caller-supplied fill. There are only
 * four verdicts a compliance surface can report and each has exactly one
 * correct token pairing; leaving the pairing to callers is how two surfaces
 * end up disagreeing about what a breach looks like.
 *
 * `warning` arrived with SE-7003: the Squad Planning Objectives tile reports
 * an at-risk objective as AMBER, which is neither a breach nor a pass and so
 * could not borrow either of those pairings without lying about the state.
 * @module components/atoms/StatusPill
 */
import React from "react";

import { Box } from "../Box";
import { Dot } from "../Dot";
import { Stack } from "../Stack";
import { Text } from "../Text";

/** The closed set of verdict tones a status chip can carry. */
export type StatusPillTone = "error" | "warning" | "success" | "neutral";

/**
 * Token pairing per tone: pill fill, marker fill, label colour.
 *
 * The marker and the label share a colour in the design — the chip reads as
 * one coloured statement rather than a coloured bullet beside grey text.
 */
const TONE_CLASS: Record<
  StatusPillTone,
  { readonly pill: string; readonly dot: string; readonly label: string }
> = {
  error: {
    pill: "bg-status-error-surface",
    dot: "bg-status-error",
    label: "text-status-error",
  },
  warning: {
    pill: "bg-status-warning-surface",
    dot: "bg-status-warning",
    label: "text-status-warning",
  },
  success: {
    pill: "bg-status-success-surface",
    dot: "bg-status-success",
    label: "text-status-success",
  },
  // No status token means "nothing has been asserted", which is exactly what
  // an unconfigured regime is — so the neutral chip is drawn from the surface
  // and content tiers rather than from a status colour that would imply a
  // verdict the club has not configured anything to produce.
  neutral: {
    pill: "bg-surface-muted",
    dot: "bg-content-muted",
    label: "text-content-muted",
  },
};

/** Props for the StatusPill atom. */
export interface StatusPillProps {
  /** Which verdict the chip reports — closed enum, tokens stay inside. */
  readonly tone: StatusPillTone;
  /** Chip text, e.g. "BREACH · 3 alerts". */
  readonly label: string;
  /** Test/a11y identity is part of the atom API (RFC standing constraint 4). */
  readonly testID?: string;
}

/**
 * Renders a filled status chip with a leading marker and a label.
 *
 * The marker is decorative: it repeats in colour what the label already says
 * in words, so it carries no accessible name of its own and assistive tech
 * reads the label instead.
 * @param root0 - Component props
 * @param root0.tone - Which verdict the chip reports
 * @param root0.label - Chip text
 * @param root0.testID - Test identifier forwarded to the chip
 * @returns The status pill element.
 */
const StatusPill = ({ tone, label, testID }: StatusPillProps) => (
  <Box
    testID={testID}
    className={`self-start rounded-full ${TONE_CLASS[tone].pill}`}
  >
    <Stack direction="horizontal" className="items-center gap-1 px-2 py-1">
      <Dot className={TONE_CLASS[tone].dot} />
      <Text variant="micro" className={TONE_CLASS[tone].label}>
        {label}
      </Text>
    </Stack>
  </Box>
);

StatusPill.displayName = "StatusPill";

export { StatusPill };
